import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import { milestones, statistics, testimonials, contactInfo, faqData } from '@/mocks/about';

const contactFormUrl = 'https://readdy.ai/api/form/d9qqc1ue3oq4jqumtor0';

export default function AboutPage() {
  const [faqOpen, setFaqOpen] = useState<number | null>(null);
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleContactSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);
    const honeypot = formData.get('phone_alt') as string;
    if (honeypot && honeypot.trim() !== '') {
      setFormStatus('success');
      return;
    }

    try {
      const body = new URLSearchParams();
      formData.forEach((value, key) => {
        if (key !== 'phone_alt' || !value) {
          body.append(key, value as string);
        }
      });

      const response = await fetch(contactFormUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      const responseText = await response.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || responseText;
        setFormError(serverMsg || '提交失败，请稍后重试');
        setFormStatus('error');
      }
    } catch {
      setFormError('网络错误，请检查连接后重试');
      setFormStatus('error');
    }
  };

  const aboutLD = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: '关于我们 - 国产视频大全',
    description: '国产视频大全品牌介绍、发展历程、用户口碑与联系方式',
    url: 'https://www.guochanshipin.com/about',
    isPartOf: {
      '@type': 'WebSite',
      name: '国产视频大全',
      url: 'https://www.guochanshipin.com/',
    },
  };

  const faqLD = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqData.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(aboutLD)}</script>
      <script type="application/ld+json">{JSON.stringify(faqLD)}</script>
      <Navbar />
      <main className="min-h-screen bg-background-50">
        {/* Hero */}
        <section className="relative pt-20 pb-14 md:pb-20 bg-background-100">
          <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
            <Breadcrumb items={[{ label: '首页', href: '/' }, { label: '关于我们' }]} />
            <div className="mt-6 text-center max-w-2xl mx-auto">
              <h1 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950 mb-4">
                关于国产视频大全
              </h1>
              <p className="text-sm md:text-base text-foreground-600 leading-relaxed">
                国产视频大全成立于2018年，是一个专注于正版高清国产视频内容的精选平台。
                我们致力于为用户提供最优质、最全面的国产视频资源，推动国产原创内容的创作与传播。
              </p>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 mt-10 md:mt-14">
              {statistics.map((stat) => (
                <div key={stat.label} className="text-center p-4 rounded-xl bg-background-50 border border-background-200/70">
                  <div className="w-9 h-9 mx-auto mb-2 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600">
                    <i className={`${stat.icon} text-lg`}></i>
                  </div>
                  <div className="font-heading text-lg md:text-xl font-bold text-foreground-950">{stat.value}</div>
                  <div className="text-xs text-foreground-500 mt-1">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Milestones */}
        <section className="py-14 md:py-20 bg-background-50">
          <div className="w-full px-4 md:px-6 max-w-4xl mx-auto">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 text-center mb-10 md:mb-14">
              发展历程
            </h2>
            <div className="relative">
              <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-0.5 bg-secondary-200 -translate-x-1/2 hidden md:block"></div>
              <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-secondary-200 md:hidden"></div>
              <div className="flex flex-col gap-8">
                {milestones.map((mile, index) => (
                  <div key={mile.year} className={`relative flex items-start gap-6 ${
                    index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'
                  }`}>
                    <div className="hidden md:flex w-1/2"></div>
                    <div className="absolute left-2 md:left-1/2 w-5 h-5 rounded-full bg-accent-500 border-4 border-background-50 -translate-x-1/2 z-10"></div>
                    <div className="ml-10 md:ml-0 md:w-1/2 bg-background-50 border border-background-200/70 rounded-xl p-4 md:p-5">
                      <span className="text-xs font-bold text-accent-600 bg-accent-100 px-2 py-0.5 rounded-full">{mile.year}</span>
                      <h3 className="font-heading font-semibold text-foreground-900 mt-2 mb-1">{mile.title}</h3>
                      <p className="text-xs md:text-sm text-foreground-600">{mile.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Testimonials */}
        <section className="py-14 md:py-20 bg-background-100">
          <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 text-center mb-10 md:mb-14">
              用户口碑
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
              {testimonials.map((t) => (
                <div key={t.id} className="bg-background-50 border border-background-200/70 rounded-xl p-5 md:p-6">
                  <div className="flex items-center gap-1 mb-3">
                    {Array.from({ length: t.rating }).map((_, i) => (
                      <i key={i} className="ri-star-fill text-accent-500 text-sm"></i>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed mb-4">{t.content}</p>
                  <div className="flex items-center gap-3">
                    <img src={t.avatar} alt={t.name} className="w-10 h-10 rounded-full object-cover" />
                    <div>
                      <p className="text-sm font-medium text-foreground-900">{t.name}</p>
                      <p className="text-xs text-foreground-500">{t.role}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-14 md:py-20 bg-background-50">
          <div className="w-full px-4 md:px-6 max-w-3xl mx-auto">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 text-center mb-10 md:mb-14">
              常见问题
            </h2>
            <div className="flex flex-col gap-3">
              {faqData.map((faq, index) => (
                <div key={index} className="rounded-xl border border-background-200/70 bg-background-50 overflow-hidden">
                  <button
                    onClick={() => setFaqOpen(faqOpen === index ? null : index)}
                    className="w-full flex items-center justify-between p-4 md:p-5 text-left cursor-pointer"
                  >
                    <h4 className="font-heading font-semibold text-sm md:text-base text-foreground-900 pr-4">{faq.question}</h4>
                    <div className={`w-6 h-6 flex items-center justify-center rounded-full transition-all duration-300 flex-shrink-0 ${
                      faqOpen === index ? 'bg-primary-500 text-white rotate-45' : 'bg-secondary-100 text-foreground-500'
                    }`}>
                      <i className="ri-add-line text-sm"></i>
                    </div>
                  </button>
                  <div className={`overflow-hidden transition-all duration-300 ${
                    faqOpen === index ? 'max-h-96 pb-4 md:pb-5 px-4 md:px-5' : 'max-h-0'
                  }`}>
                    <p className="text-sm text-foreground-600 leading-relaxed">{faq.answer}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Contact */}
        <section className="py-14 md:py-20 bg-background-100">
          <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
            <h2 id="contact" className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 text-center mb-10 md:mb-14">
              <a href="#contact">联系我们</a>
            </h2>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-10 max-w-5xl mx-auto">
              <div className="flex flex-col gap-4">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 flex-shrink-0">
                    <i className="ri-mail-line text-lg"></i>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground-900">电子邮箱</p>
                    <p className="text-sm text-foreground-600">{contactInfo.email}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 flex-shrink-0">
                    <i className="ri-phone-line text-lg"></i>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground-900">客服电话</p>
                    <p className="text-sm text-foreground-600">{contactInfo.phone}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 flex-shrink-0">
                    <i className="ri-map-pin-line text-lg"></i>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground-900">公司地址</p>
                    <p className="text-sm text-foreground-600">{contactInfo.address}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 flex-shrink-0">
                    <i className="ri-time-line text-lg"></i>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground-900">工作时间</p>
                    <p className="text-sm text-foreground-600">{contactInfo.workHours}</p>
                  </div>
                </div>
              </div>

              <div className="bg-background-50 border border-background-200/70 rounded-xl p-5 md:p-6">
                <h3 className="font-heading font-semibold text-foreground-950 mb-4">发送消息</h3>
                <form onSubmit={handleContactSubmit} data-readdy-form="">
                  <div className="flex flex-col gap-3">
                    <div>
                      <input
                        type="text"
                        name="name"
                        placeholder="您的姓名"
                        required
                        className="w-full px-4 py-2.5 rounded-lg border border-background-200/70 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 transition-colors"
                      />
                    </div>
                    <div>
                      <input
                        type="email"
                        name="email"
                        placeholder="您的邮箱"
                        required
                        className="w-full px-4 py-2.5 rounded-lg border border-background-200/70 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 transition-colors"
                      />
                    </div>
                    <div>
                      <textarea
                        name="message"
                        placeholder="请输入您的留言内容..."
                        required
                        maxLength={500}
                        rows={4}
                        className="w-full px-4 py-2.5 rounded-lg border border-background-200/70 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 transition-colors resize-none"
                      ></textarea>
                    </div>
                    <input
                      type="text"
                      name="phone_alt"
                      tabIndex={-1}
                      autoComplete="off"
                      aria-hidden="true"
                      readOnly
                      className="absolute left-[-9999px] opacity-0 h-0 w-0 overflow-hidden pointer-events-none"
                      style={{ position: 'absolute', left: '-9999px', opacity: 0, height: 0, width: 0, overflow: 'hidden', pointerEvents: 'none' }}
                    />
                    <button
                      type="submit"
                      className="w-full py-2.5 bg-primary-500 hover:bg-primary-600 text-white font-medium rounded-lg text-sm transition-colors cursor-pointer whitespace-nowrap"
                    >
                      提交留言
                    </button>
                  </div>

                  {formStatus === 'success' && (
                    <p className="mt-3 text-sm text-green-600">感谢您的留言，我们会尽快回复！</p>
                  )}
                  {formStatus === 'error' && (
                    <p className="mt-3 text-sm text-red-600">{formError}</p>
                  )}
                </form>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}